import {
  Button,
  CreateButton,
  Dropdown,
  ExportButton,
  Icons,
  List,
  Menu,
  Popconfirm,
  ShowButton,
  Space,
  Table,
  TableProps,
  Tooltip,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { IResourceComponentsProps, useCan, useNavigation } from '@pankod/refine-core';
import { SearchWithDateRange } from 'components/search/SearchWithDateRange';
import CustomTableWithPagination from 'components/table/custom-table';
import { EReturnStatus } from 'enums';
import { IQueryOptionals, IReturn, PaginationHandler } from 'interfaces';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchIndex } from 'services/meilisearch';
import { deleteReturn, getReturns, getReturnsDropdown, updateReturnStatus } from 'services/return';
import { getActivityHistoryUrl } from 'utilities/activity-log';
import { downloadCSVFile } from 'utilities/file';
import { formatCurrency, getDateRangeOfLastXMonths } from 'utilities/shared';
import styles from './style.module.css';
import MetaData from 'utilities/metadata';

const { Text } = Typography;

export const ReturnList: React.FC<IResourceComponentsProps> = () => {
  const [returns, setReturns] = useState<IReturn[]>([]);
  const [totalReturns, setTotalReturns] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExportButtonLoading, setIsExportButtonLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>(() => {
    const [startDate, endDate] = getDateRangeOfLastXMonths(3);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  });
  const { push } = useNavigation();
  const [actionsDisable, setActionsDisable] = useState<Record<string, boolean>>({});
  const { data: canAccessDelete } = useCan({
    resource: 'returns',
    action: 'delete',
    params: { field: 'id' },
  });
  const query = useMemo<IQueryOptionals>(() => {
    return {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      searchString: searchText,
      start: page,
      limit,
    };
  }, [dateRange.startDate, dateRange.endDate, searchText, page, limit]);

  const fetchReturns = useCallback(async (query: IQueryOptionals) => {
    setIsLoading(true);
    try {
      const returnsResponse = await getReturns(query);
      setReturns(returnsResponse.data);
      setTotalReturns(returnsResponse.total);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchReturns(query);
  }, [fetchReturns, query]);

  const deleteConfirmHandler = async (id: string) => {
    try {
      await deleteReturn(id);
      fetchReturns({ startDate: query.startDate, endDate: query.endDate });
      notification.success({
        message: 'Success',
        description: 'Successfully deleted a return',
      });
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to delete return',
      });
    }
  };

  const onSearch = async (values: {
    startDate?: string;
    endDate?: string;
    searchString: string;
  }) => {
    setDateRange({ startDate: values.startDate, endDate: values.endDate });
    setSearchText(values.searchString);
    setPage(1);
  };

  const onExportButtonClick = async () => {
    setIsExportButtonLoading(true);
    try {
      const allReturnsData = await getReturnsDropdown(query.startDate, query.endDate);

      const rows = [['ID', 'Client Name', 'Date', 'Warehouse', 'Total Amount']];
      allReturnsData.forEach((element) => {
        const clientName = element.client.name;
        const warehouse = element.warehouse.name;
        rows.push([
          element.id,
          clientName,
          element.returnDate.toString(),
          warehouse,
          element.totalAmount.toString(),
        ]);
      });
      downloadCSVFile(rows);
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to Export CSV File.',
      });
    }
    setIsExportButtonLoading(false);
  };

  const pageHeaders = (
    <Space>
      <CreateButton type="primary" />
      <ExportButton loading={isExportButtonLoading} onClick={onExportButtonClick} />
      <Button
        icon={<Icons.HistoryOutlined />}
        onClick={() => push(getActivityHistoryUrl('return'))}
      >
        Activity
      </Button>
    </Space>
  );

  const onTableChange: TableProps<IReturn>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    const page = pagination.current ?? 1;
    const limit = pagination.pageSize ?? 10;
    setPage(page);
    setLimit(limit);
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  return (
    <List title="Returns" headerProps={{ extra: pageHeaders }}>
      <MetaData />
      <SearchWithDateRange
        placeholder="Search by Client, ID, Warehouse"
        size="large"
        className={styles['filter-section__search']}
        index={SearchIndex.Return}
        groups={['client', 'id', 'warehouse']}
        onSearch={onSearch}
      />
      <CustomTableWithPagination
        total={totalReturns}
        page={page}
        limit={limit}
        onPageChange={onPageChange}
      >
        <Table
          dataSource={returns}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: true }}
          onChange={onTableChange}
          // pagination={{
          //   defaultPageSize: 10,
          //   showSizeChanger: true,
          //   total: totalReturns,
          //   current: page,
          //   pageSize: limit,
          // }}
          pagination={false}
        >
          <Table.Column<IReturn> dataIndex="id" title="ID" />
          <Table.Column<IReturn> dataIndex={['client', 'name']} title="Client Name" />
          <Table.Column<IReturn>
            dataIndex="returnDate"
            title="Date"
            render={(_, record): React.ReactNode => {
              const date = new Date(record.returnDate).toLocaleDateString('en-GB');
              return <Text>{date}</Text>;
            }}
          />
          <Table.Column<IReturn> dataIndex={['warehouse', 'name']} title="Warehouse" />
          <Table.Column<IReturn>
            dataIndex="totalAmount"
            title="Total Amount"
            render={(_, record) => formatCurrency(record.totalAmount)}
          />
          <Table.Column<IReturn>
            title="Actions"
            dataIndex="actions"
            width={120}
            render={(_text, record): React.ReactNode => {
              return (
                <Space>
                  <ShowButton size="small" recordItemId={record.id} hideText />
                  <Popconfirm
                    title="Are you sure?"
                    okText="Delete"
                    onConfirm={() => deleteConfirmHandler(record.id)}
                    disabled={!canAccessDelete?.can}
                  >
                    <Button
                      size="small"
                      disabled={!canAccessDelete?.can}
                      icon={<Icons.DeleteOutlined />}
                      danger
                    />
                  </Popconfirm>
                  <Tooltip title={<span>User: {record.user ? record.user.name : 'N/A'}</span>}>
                    <Button size="small" icon={<Icons.InfoOutlined />} />
                  </Tooltip>
                  <StatusUpdateAction record={record} onStatusUpdate={() => fetchReturns(query)} disabled={actionsDisable[record.id]} setActionsDisable={setActionsDisable} />
                </Space>
              );
            }}
          />
        </Table>
      </CustomTableWithPagination>
    </List>
  );
};

type StatusUpdateActionProps = {
  record: IReturn;
  onStatusUpdate: () => Promise<void>;
  disabled: boolean;
  setActionsDisable: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
};

const StatusUpdateAction: React.FC<StatusUpdateActionProps> = ({ record, onStatusUpdate, disabled, setActionsDisable }) => {
  const updateStatus = async (id: string, status: EReturnStatus) => {
    setActionsDisable((prev) => ({ ...prev, [id]: true }));
    try {
      await updateReturnStatus(id, status);
      onStatusUpdate();
      notification.success({
        message: 'Success',
        description: 'Successfully changed status',
      });
    } catch (_) {
      notification.error({
        message: 'Failed',
        description: 'Failed to change status',
      });
    } finally {
      setActionsDisable(({ [id]: _, ...rest }) => rest);
    }
  };

  const getDisabledState = (status: EReturnStatus) => {
    switch (status) {
      case EReturnStatus.CONFIRMED:
        return {
          confirmedDisabled: true,
          completedDisabled: false,
          cancelledDisabled: false,
        };
      case EReturnStatus.COMPLETED:
        return {
          confirmedDisabled: true,
          completedDisabled: true,
          cancelledDisabled: false,
        };
      case EReturnStatus.CANCELLED:
        return {
          confirmedDisabled: true,
          completedDisabled: true,
          cancelledDisabled: true,
        };
      default:
        return {
          confirmedDisabled: false,
          completedDisabled: false,
          cancelledDisabled: false,
        };
    }
  };

  const moreMenu = (record: IReturn) => {
    const { confirmedDisabled, completedDisabled, cancelledDisabled } = getDisabledState(
      record.returnStatus as EReturnStatus
    );
    return (
      <Menu mode="vertical" onClick={({ domEvent }) => domEvent.stopPropagation()}>
        <Menu.Item
          key="confirmed"
          disabled={confirmedDisabled || disabled}
          onClick={() => updateStatus(record.id, EReturnStatus.CONFIRMED)}
        >
          Confirmed
        </Menu.Item>
        <Menu.Item
          key="completed"
          disabled={completedDisabled || disabled}
          onClick={() => updateStatus(record.id, EReturnStatus.COMPLETED)}
        >
          Completed
        </Menu.Item>
        <Popconfirm
          title="Are you sure?"
          okText="Yes"
          onConfirm={() => updateStatus(record.id, EReturnStatus.CANCELLED)}
          cancelText="No"
          disabled={cancelledDisabled || disabled}
        >
          <Menu.Item
            key="cancelled"
            disabled={cancelledDisabled || disabled}
          >
            Cancelled
          </Menu.Item>
        </Popconfirm>

      </Menu>
    );
  };
  return (
    <Dropdown overlay={moreMenu(record)} trigger={['click']}>
      <Icons.MoreOutlined
        onClick={(e) => e.stopPropagation()}
        style={{
          fontSize: 24,
        }}
      />
    </Dropdown>
  );
};
